<template>
  <div
    class="d-widget-chart"
    :class="{'d-widget-chart--card': cardMode}"
    :data-id="item.id"
    :data-widget-load="loading ? 'loading' : 'load'"
    @mouseenter="showAction = true"
    @mouseleave="showAction = false"
  >
    <!-- header -->
    <div class="d-widget-chart-header" v-if="item.type !== 'content'">
      <fts-widget-title
        :modules="modules"
        :module-id="item.moduleId"
        :listing-id="item.listingId"
        :widget-name="item.name"
        :preview="preview"
        @click:module="routeListing"
        @click:listing="routeListing"
      />
    </div>
    <!-- body -->
    <div class="d-widget-chart-body" ref="chartBody">
      <!-- bar chart -->
      <fts-bar-chart
        :height="chartHeight"
        :stacked="item.type === 'stackedBar'"
        :chart-options="chartOptions"
        :chart-data="chartData"
        :key="chartHeight"
        @segment-clicked="segmentClicked"
        ref="chart"
        v-if="item.type === 'bar' || item.type === 'stackedBar'"
      />
      <!-- horizontal bar chart -->
      <fts-bar-chart
        :height="chartHeight"
        horizontal
        :stacked="item.type === 'stackedBarHorizontal'"
        :chart-options="chartOptions"
        :chart-data="chartData"
        :key="chartHeight"
        @segment-clicked="segmentClicked"
        ref="chart"
        v-else-if="item.type === 'barHorizontal' || item.type === 'stackedBarHorizontal'"
      />
      <!-- pie chart -->
      <fts-pie-chart
        :height="chartHeight"
        :chart-options="chartOptions"
        :chart-data="chartData"
        :key="chartHeight"
        @segment-clicked="segmentClicked"
        ref="chart"
        v-else-if="item.type === 'pie'"
      />
      <!-- line chart -->
      <fts-line-chart
        :height="chartHeight"
        :chart-options="chartOptions"
        :chart-data="chartData"
        :key="chartHeight"
        @segment-clicked="segmentClicked"
        ref="chart"
        v-else-if="item.type === 'line'"
      />
      <!-- doughnut chart -->
      <fts-doughnut-chart
        :height="chartHeight"
        :chart-options="chartOptions"
        :chart-data="chartData"
        :key="chartHeight"
        @segment-clicked="segmentClicked"
        ref="chart"
        v-else-if="item.type === 'doughnut'"
      />
      <!-- content -->
      <div class="d-widget-chart-content" v-else-if="item.type === 'content'">
        <img :src="contentFilePath" alt="content" />
      </div>
    </div>
    <!-- footer -->
    <div class="d-widget-chart-footer">
      <!-- aggregate -->
      <div class="d-widget-chart-aggregate" v-if="chartData && !item.noTotal && (chartData.total || chartData.total === 0)">
        Total {{item.aggregate}}
      </div>
      <!-- summary -->
      <div class="d-widget-chart-summary">
        <template v-if="chartData && !item.noTotal && (chartData.total || chartData.total === 0)">
          <!-- total -->
          <fts-tippy :content="getFormatNumber()" class="d-widget-chart-total">
            <template #trigger>{{getAbbreviateNumber()}}</template>
          </fts-tippy>
          <!-- information -->
          <div class="d-widget-chart-information" v-if="item.displayName">
            <i class="d-widget-chart-information-icon" :class="getInformationIcon()" v-if="getInformationIcon()"></i>
            <div class="d-widget-chart-information-name">{{item.displayName}}</div>
          </div>
        </template>
        <!-- last update -->
        <div class="d-widget-chart-last-update" v-if="!preview && lastUpdated">
          last updated
          <fts-tippy
            :content="moment(lastUpdated).format('MMM D, h:mm:ss A')"
            class="d-widget-chart-last-update-date"
          >
            <span slot="trigger">
              {{lastUpdatedHumanize}}
            </span>
          </fts-tippy>
        </div>
      </div>
    </div>
    <!-- error -->
    <div class="d-widget-overlay d-widget-overlay--error" v-if="isError">
      <!-- AutoCount Full's lock (402 connector_full_required): the listing's card, smaller -->
      <div class="d-widget-error" v-if="connectorLock">
        <fts-connector-lock-card :lock="connectorLock" compact />
      </div>
      <div class="d-widget-error" v-else>
        <div class="d-widget-error-title">
          {{error.title}}
        </div>
        <div class="d-widget-error-description">
          {{error.description}}
        </div>
      </div>
    </div>
    <!-- data exceed -->
    <div class="d-widget-overlay d-widget-overlay--exceed" v-if="isDataExceed">
      <div class="d-widget-error">
        <div class="d-widget-error-title">
          {{dataExceedWarn.title}}
        </div>
        <div class="d-widget-error-description">
          {{dataExceedWarn.description}}
        </div>
      </div>
    </div>
    <!-- action -->
    <fts-widget-action :show="showAction" v-if="!preview">
      <!-- expand -->
      <fts-btn
        text
        icon
        sm
        @click="expand"
        v-if="!loading && !isError && !isDataExceed && item.type !== 'content'"
      >
        <font-awesome-icon :icon="['fas', 'expand-alt']" />
      </fts-btn>
      <!-- refresh -->
      <fts-btn
        text
        icon
        sm
        :disabled="loading"
        @click="refresh"
      >
        <font-awesome-icon :icon="['fas', 'sync-alt']" />
      </fts-btn>
      <!-- configure -->
      <fts-btn
        text
        icon
        sm
        :disabled="loading"
        @click="configure"
        v-if="isConfigurable"
      >
        <font-awesome-icon :icon="['fas', 'cog']" />
      </fts-btn>
      <!-- delete -->
      <fts-btn
        text
        icon
        sm
        :disabled="loading"
        @click="deleteConfirmation"
        v-if="isConfigurable"
      >
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
    </fts-widget-action>
    <!-- load -->
    <div class="d-widget-overlay" v-if="loading">
      <fts-spinner show :size="24" />
    </div>
    <!-- chart modal -->
    <chart-modal
      append-to-body
      :modules="modules"
      :item="item"
      :chart-data="chartData"
      :last-updated="lastUpdated"
      :last-updated-humanize="lastUpdatedHumanize"
      :loading="loading"
      @chart:refresh="refresh"
      ref="chartModal"
    />
  </div>
</template>

<script>
import "luxon";
import "chartjs-adapter-luxon";
import moment from "moment";

import { authorizationStore } from "../../store/authorizationStore";
import { clientStore } from "../../store/clientStore";
import commons from "../../commons/commons";
import queryLogic from "../../commons/queryLogic";
import { Themes } from "../../commons/colors";
import { responseErrorMessage, widgetDataExceed, Widget_Error } from "../../commons/message";

import ChartModal from "./ChartModal";
import FTSWidgetTitle from "../FTSWidgetTitle";
import FTSWidgetAction from "../FTSWidgetAction";
import FTSBarChart from "../../elements/chart/FTSBarChart";
import FTSPieChart from "../../elements/chart/FTSPieChart";
import FTSLineChart from "../../elements/chart/FTSLineChart";
import FTSDoughnutChart from "../../elements/chart/FTSDoughnutChart";
import FTSBtn from "../../elements/FTSBtn";
import FTSSpinner from "../../elements/FTSSpinner";
import FTSTippy from "../../elements/FTSTippy";
import FTSConnectorLockCard from "../../elements/FTSConnectorLockCard";
import { connectorLockFromError } from "../../elements/connectorLock";

const DEFAULT_CHART_HEIGHT = 260;

export default {
  components: {
    "chart-modal": ChartModal,
    "fts-widget-title": FTSWidgetTitle,
    "fts-widget-action": FTSWidgetAction,
    "fts-bar-chart": FTSBarChart,
    "fts-pie-chart": FTSPieChart,
    "fts-line-chart": FTSLineChart,
    "fts-doughnut-chart": FTSDoughnutChart,
    "fts-btn": FTSBtn,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy,
    "fts-connector-lock-card": FTSConnectorLockCard,
  },
  props: {
    modules: Array,
    states: Array,
    users: Array,
    item: Object,
    readonly: Boolean,
    preview: Boolean,
    filters: Object,
    cardMode: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    error: {
      title: Widget_Error.Title,
      description: Widget_Error.Description
    },
    chartHeight: DEFAULT_CHART_HEIGHT,
    chartData: {
      labels: [],
      datasets: []
    },
    chartOptions: {},
    loading: false,
    isError: false,
    // the widget's module is on AutoCount Full and this workspace is not (402)
    connectorLock: null,
    isDataExceed: false,
    showAction: false,
    lastUpdated: null,
    lastUpdatedHumanize: null,
  }),
  methods: {
    moment,
    updateChart() {
      this.$refs.chart.updateChart();
    },
    segmentClicked(payload) {
      this.$emit('segment-clicked', { item: this.item, payload: payload })
    },  
    async refresh() {
      this.loading = true;
      this.isError = false;
      try {
        let res = null;

        let chartItem = _.cloneDeep(this.item);
        chartItem.dashboardFilters = this.filters;

        if (this.preview) {
          res = await clientStore.dispatch('previewWidget', this.item);
        } else {
          res = await clientStore.dispatch('retrieveWidget', { itemId: this.item.id, filters: this.filters });
        }
        
        if (Boolean(res.data)) {
          this.chartData = res.data;
          let limit = 500;

          if (this.item.limit) {
            limit = this.item.limit;
          }

          // A module reading an external source (AutoCount) is summarised in the source over every row: its
          // numbers are complete, so the plan's entry limit does not hide the chart.
          this.isDataExceed = !this.chartData.external && this.chartData.count > limit;

          if (this.chartData.datasets) {
            let colors = null;
            let isCustomColor = false;

            if (this.item.colorCodes && this.item.colorCodes.length) {
              isCustomColor = true;
              colors = this.item.colorCodes;
            }

            const workspaceStates = this.states || clientStore.getters.workspaceStates;
            const workspaceUsers = this.users || clientStore.getters.workspaceUsers;
            /* SS05396: assignTo is a user field too - without it the chart labels stay as raw
               usernames instead of the assignee's display name. Unlike the other user fields it
               can be empty, so the mapping falls back to "(no value)". */
            const userInformationIds = ['assignTo', 'createdBy', 'lastChangeBy', 'username'];
            /* A Select or Tag group or breakdown key arrives already laid out in the sequence
               its options were designed in, with that sequence alongside it. Seating every
               slice by the option it belongs to - rather than by where it happens to land this
               refresh - keeps a colour with its option when a filter empties one, and lines a
               custom colour list up with the options it was typed against. */
            const optionSequence = Array.isArray(this.chartData.optionSequence)
              ? this.chartData.optionSequence
              : null;
            const breakdownOptionSequence = Array.isArray(this.chartData.breakdownOptionSequence)
              ? this.chartData.breakdownOptionSequence
              : null;
            const optionSeat = (sequence, value) => {
              if (!sequence) return -1;

              const label = value === null || value === undefined ? "" : String(value).trim();
              return sequence.findIndex(y => (y || "").trim() === label);
            };
            const optionSeats = optionSequence
              ? this.chartData.labels.map(x => optionSeat(optionSequence, x))
              : null;

            /* numerical order ascending - skipped for a designed option sequence, which is an
               order of its own even when every option reads as a number, and for a widget with
               its own sort (SS05554), whose buckets the server has already laid out along it */
            if (!optionSequence && !this.item.sort && this.chartData.labels.every(x => commons.isNumber(x))) {
              const sortData = this.chartData.labels
                .map((value, index) => ({ 'label': value, 'index': index }))
                .sort((a, b) => a.label - b.label);

              this.chartData.labels = sortData.map(x => x.label);

              for(let i = 0; i < this.chartData.datasets.length; i++) {
                let data = [];
                for(let sortDataInd = 0; sortDataInd < sortData.length; sortDataInd++) {
                  data.push(this.chartData.datasets[i].data[sortData[sortDataInd].index]);
                }
                this.chartData.datasets[i].data = data;
              }
            }

            if (this.item.groupByInformationId === 'state') { // state
              if (!isCustomColor && !this.item.breakdownByInformationId) {
                colors = [];
              }

              this.chartData.labels = this.chartData.labels.map(x => {
                const res = workspaceStates.find(y => y.id === x);

                if (!isCustomColor && !this.item.breakdownByInformationId) {
                  colors.push(res && res.color || Themes.light.gray400);
                }

                return res && res.name || x;
              });
            } else if (userInformationIds.indexOf(this.item.groupByInformationId) > -1) { // user
              this.chartData.labels = this.chartData.labels.map(x => {
                const res = workspaceUsers.find(y => y.username === x);
                if (res && res.displayName) {
                  return res.displayName.trim();
                }

                return (x && x.trim()) || "(no value)";
              });
            } else if (this.chartData.labels.some(x => (moment(x, moment.ISO_8601).isValid()))) { // date
              this.chartData.labels = this.chartData.labels.map(x => {
                return moment(x).format('YYYY-MM-DD');
              });
            } else { // default
              this.chartData.labels = this.chartData.labels.map(label => {
                if (label === true) return "Yes";
                if (label === false) return "No";

                const trimmedLabel = label && label.trim();
                return trimmedLabel || "(no value)";
              });
            }

            const defaultColors = [
              "#2B9EC6",
              "#73CADF",
              "#A4CE3A",
              "#ffeb3b",
              "#F59B05",
              "#EF5350",
              "#AB47BC",
              "#7E57C2",
              "#5C6bC0",
              "#42A5F5",
              "#26A69A",
              "#4caf50",
              "#8d6e63",
              "#78909c"
            ];

            this.chartData.datasets.forEach((x, ind) => {
              const rawLabel = x.label;

              if (this.item.breakdownByInformationId === "state") { // state
                const findState = workspaceStates.find(y => y.id === x.label);
                if (findState && findState.name) {
                  x.label = findState.name;
                }
              } else if (userInformationIds.indexOf(this.item.breakdownByInformationId) > -1) { // user
                const findUser = workspaceUsers.find(y => y.username === x.label);
                if (findUser && findUser.displayName) {
                  x.label = findUser.displayName.trim();
                } else {
                  /* SS05396: an entry with no assignee breaks down into a null bucket, so the
                     legend drew a blank swatch - name it the way every other empty value is
                     named. A username the workspace no longer lists stays as it is. */
                  x.label = (x.label && x.label.trim()) || "(no value)";
                }
              } else if (x.label === null || x.label === undefined || x.label.trim() === "") {
                /* SS05412: without a breakdown the dataset label is the group-by field name, not
                   an entry value, so "(no value)" is the wrong fallback. Servers that predate the
                   fix only named State and Created Date - resolve the rest of the system fields
                   here. Custom information always arrives named. */
                const groupBy = !this.item.breakdownByInformationId
                  && queryLogic.querySelectionPrefix
                    .find(y => y.id === this.item.groupByInformationId);

                x.label = (groupBy && groupBy.displayName) || "(no value)";
              }

              const chartColors = colors || defaultColors;
              const breakdownSeat = optionSeat(breakdownOptionSequence, rawLabel);
              let color = chartColors;

              /* one dataset holding every option: each slice takes the colour of its own
                 seat in the designed sequence, not of the position it landed in */
              if (optionSeats && !this.item.breakdownByInformationId) {
                color = optionSeats.map((seat, labelIndex) =>
                  chartColors[(seat > -1 ? seat : labelIndex) % chartColors.length]);
              }

              if (
                this.chartData.datasets.length == 1 &&
                this.item.type === "line"
              ) {
                color = defaultColors[ind % defaultColors.length];
                x.borderColor = color;
              } else if (
                (this.chartData.datasets.length > 1 ||
                  /* SS05397: a breakdown that resolves to a single value still produces just
                     one dataset, which no branch here matched - so the whole palette ARRAY
                     stayed on backgroundColor and Chart.js coloured each BAR separately. The
                     chart showed several colours for what is one breakdown value. Colour
                     encodes the breakdown series, so one dataset means one colour. Charts with
                     NO breakdown keep their per-bar palette on purpose - that is the optionSeats
                     branch above. */
                  this.item.breakdownByInformationId) &&
                this.item.type !== "pie" &&
                this.item.type !== "doughnut"
              ) {
                /* the breakdown option this dataset stands for owns the colour, so a run with
                   one option missing does not hand its colour to the next one along */
                const seat = breakdownSeat > -1 ? breakdownSeat : ind;
                color = chartColors[seat % chartColors.length];
                if (this.item.type === "line") {
                  x.borderColor = color;
                }
              }

              x.backgroundColor = color;
            }); 
          }
          // handle no data
          if (
            !this.chartData.datasets ||
            !this.chartData.datasets.length
          ) {
            this.chartData.datasets = [
              {
                label: 'No Data',
                data: [0],
                borderColor: 'transparent',
                backgroundColor: 'transparent',
              }
            ]
          }

          if (this.chartData.count === null || this.chartData.count === undefined) {
            this.chartData.count = 0;
          }

          if (this.chartData.total === null || this.chartData.total === undefined) {
            this.chartData.total = 0;
          }

          /* last updated */
          this.lastUpdated = moment();
          this.getLastUpdatedHumanize();
          /* computed chart height */
          this.$nextTick(() => {
            this.updateChartHeight();
          });
        } else {
          throw 'error';
        }
      } catch(err) {
        console.error(err);
        this.error = {
          title: Widget_Error.Title,
          description: responseErrorMessage({
            error: err,
            message: Widget_Error.Description
          })
        };
        this.connectorLock = connectorLockFromError(err);
        this.isError = true;
      } finally {
        this.loading = false;
      }
    },
    updateChartHeight() {
      try {
        const exceedWarnEl = this.$refs.exceedWarn;
        let chartHeight = this.$refs.chartBody.clientHeight;
        if (this.isDataExceed && exceedWarnEl) {
          try {
            chartHeight -= (exceedWarnEl.offsetHeight + parseInt(window.getComputedStyle(exceedWarnEl).marginBottom));
          } catch(err) {
            console.warn("[DOMException]", err);
          }
        }
        this.chartHeight = chartHeight;
      } catch(err) {
        console.warn("[DOMException]", err);
        this.chartHeight = DEFAULT_CHART_HEIGHT;
      }
    },
    routeListing() {
      this.$emit("route:listing", {
        moduleId: this.item.moduleId,
        listId: this.item.listingId
      });
    },
    expand() {
      this.$refs.chartModal.show();
    },
    configure() {
      this.$emit("configure", {
        refresh: this.refresh
      });
    },
    deleteConfirmation() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete Chart?`,
        buttons: [
          { title: "Cancel"},
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.$emit("delete", this.item.id);
            }
          }
        ]
      });
    },
    getFormatNumber() {
      return commons.formatNumber(
        this.chartData.total,
        {
          currency: Boolean(this.item.informationType && this.item.informationType === 4),
        }
      );
    },
    getAbbreviateNumber() {
      return commons.formatNumber(
        this.chartData.total,
        {
          currency: Boolean(this.item.informationType && this.item.informationType === 4),
          abbreviate: true,
        }
      );
    },
    getInformationIcon() {
      return commons.informationIcon(this.item.informationType);
    },
    getLastUpdatedHumanize() {
      this.lastUpdatedHumanize = commons.momentRelativeTime(this.lastUpdated);
    }
  },
  computed: {
    isConfigurable() {
      return !this.readonly && authorizationStore.getters.isAdminUser;
    },
    contentFilePath() {
      if(this.chartData.value) {
        return commons.getFilePath(this.chartData.value.path, `${this.item.moduleId}-${this.item.groupByInformationId}`);
      }

      return 'https://inistate.com/assets/inistate-logo.png';
    },
    dataExceedWarn() {
      return widgetDataExceed(this.item && this.item.limit);
    }
  },
  mounted() {
    window.eventBus.$on('refreshChartWidget', this.refresh); 
    window.eventBus.$on('refreshChartWidgetLastUpdated', this.getLastUpdatedHumanize);
    window.eventBus.$on('dashboard:resize', this.updateChartHeight);
    this.refresh();
  },
  beforeDestroy() {
    window.eventBus.$off('refreshChartWidget', this.refresh);
    window.eventBus.$off('refreshChartWidgetLastUpdated', this.getLastUpdatedHumanize);
    window.eventBus.$off('dashboard:resize', this.updateChartHeight);
  }
}
</script>

<style lang="scss" scoped>
@use "./src/components/dashboard/widgets/scss/style";
</style>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.d-widget-chart {
  position: relative;
  display: flex;
  flex-direction: column;
  color: var(--fts-text);
  .d-widget-chart-aggregate {
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
    color: var(--fts-default);
    text-transform: capitalize;
  }
  .d-widget-chart-summary {
    width: 100%;
    display: flex;
    align-items: flex-end;
    cursor: default;
  }
  .d-widget-chart-total {
    font-weight: 500;
    font-size: 1.5rem;
    line-height: 1.5rem;
    cursor: default;
  }
  .d-widget-chart-information {
    min-width: 0;
    display: flex;
    align-items: center;
    margin-left: 0.5rem;
    font-size: 0.875rem;
    .d-widget-chart-information-icon {
      margin-right: 0.125rem;
      color: var(--fts-secondary);
    }
    .d-widget-chart-information-name {
      @include text-overflow-ellipsis;
    }
  }
  .d-widget-chart-last-update {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-left: 1rem;
    white-space: nowrap;
    font-size: 0.875rem;
    color: var(--fts-default);
    .d-widget-chart-last-update-date {
      margin-left: 0.25rem;
      font-size: 0.875rem;
    }
  }
  .d-widget-data-exceed-warn {
    margin-bottom: 0.5rem;
  }
  .d-widget-chart-content {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    > img {
      max-height: 100%;
      max-width: 100%;
      object-fit: cover;
    }
  }
  .d-widget-chart-header {
    margin-bottom: 0.75rem;
  }
  .d-widget-chart-body {
    flex: 1;
    overflow: hidden;
  }
  .d-widget-chart-footer {
    margin-top: 0.75rem;
  }
  &.d-widget-chart--card {
    padding: 0.75rem;
    border-radius: 0.5rem;
    box-shadow: var(--fts-elevation-2);
    background-color: var(--fts-card-background);
  }
}
</style>